import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Tag } from 'antd';
import cs from 'classnames';

import { FormattedOption } from 'types/dictionaries';

import styles from './Tags.module.scss';

interface TagsProps {
  dictionary: FormattedOption[];
  values?: string[] | null;
  isTextMode?: boolean;
  className?: string;
  maxVisible?: number;
}

const Tags: FC<TagsProps> = ({
  dictionary,
  values,
  isTextMode,
  className,
  maxVisible
}) => {
  const { t } = useTranslation();

  const { labels, uniqueValues } = useMemo(() => {
    const unique = Array.from(new Set(values ?? []));
    const labels = unique.map((value) => {
      const match = dictionary.find((item) => item.value === value);
      return match ? { label: match.label } : { label: value };
    });
    return { labels, uniqueValues: unique };
  }, [dictionary, values]);

  if (isTextMode) {
    return labels.length ? (
      <div className={cs(styles.tagContainer, className)}>
        {labels.map(({ label }) => label).join(', ')}
      </div>
    ) : null;
  }

  const total = labels.length;
  const valuesKey = uniqueValues.join('\u0000');

  const [isExpanded, setIsExpanded] = useState(false);
  const shouldCollapse = Boolean(maxVisible && maxVisible > 0);
  const visibleCount = shouldCollapse
    ? Math.min(maxVisible as number, total)
    : total;
  const hiddenCount = shouldCollapse && !isExpanded ? total - visibleCount : 0;

  useEffect(() => {
    if (!shouldCollapse) {
      return;
    }
    setIsExpanded(false);
  }, [shouldCollapse, total, valuesKey]);

  const visibleLabels =
    shouldCollapse && !isExpanded ? labels.slice(0, visibleCount) : labels;

  return labels.length ? (
    <div className={cs(styles.tagContainer, className)}>
      {visibleLabels.map(({ label }, index) => (
        <Tag bordered={false} key={uniqueValues[index]}>
          {label}
        </Tag>
      ))}

      {shouldCollapse && !isExpanded && hiddenCount > 0 && (
        <Tag
          bordered={false}
          className={styles.toggleTag}
          onClick={() => setIsExpanded(true)}
        >
          +{hiddenCount}
        </Tag>
      )}

      {shouldCollapse && isExpanded && total > visibleCount && (
        <Button
          type="text"
          className={styles.lessTag}
          onClick={() => setIsExpanded(false)}
        >
          {t('common.showLess')}
        </Button>
      )}
    </div>
  ) : null;
};

export default Tags;
